import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import I18nHOC from "UIComponents/I18nHOC";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import classes from "./TaggedPracticesV2.scss";
import classNames from "classnames";
import { StarFilled, TickCircleFilled } from "@toddle-edu/ds-icons";
import { Tag, Tooltip } from "@toddle-edu/ds-web";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import styles from "./TaggedPracticesV2Styles";
import ACLStore from "lib/aclStore";
import { ENABLE_AUTH_AND_EVAL_REVAMP } from "Constants/permissionConstants";
import { getSnPEvaluationCyclesBasicDetailsFromCache } from "SnP_v2/modules/SnPGraphqlHelpers";
import { getSettingValue } from "modules/PermissionModule";
import { Trans } from "react-i18next";
import { getTypeLabel } from "SnP_v2/modules/SnPUtils";

const ApprovedPracticeMessage = props => {
  const {
    itemTypeLabel,
    itemTitle,
    cycleType,
    cycleName,
    isAuthAndEvalRevampEnabled,
  } = props;
  return isAuthAndEvalRevampEnabled ? (
    <Tooltip
      dsVersion="2.0"
      tooltip={
        <Trans i18nKey={"snp:evidence_approved_for_practice_label"}>
          {{ evidenceTypeLabel: itemTypeLabel }}

          <div className={classes.highlightText}>{{ title: itemTitle }}</div>

          {{ cycleType: _.toLower(cycleType) }}

          <div className={classes.highlightText}>{{ cycleName }}</div>
        </Trans>
      }
      overlayInnerStyle={styles.updatedTooltipContainerStyle}
      placement={"bottom"}
    >
      <div className={classes.approvedPracticeForAuthAndEvalRevamp}>
        <TickCircleFilled size={"xxx-small"} variant="success" />
      </div>
    </Tooltip>
  ) : (
    <LinkWithTooltip
      tooltip={
        <Trans i18nKey={"snp:evidence_approved_for_practice_label"}>
          {{ evidenceTypeLabel: itemTypeLabel }}

          <div className={classes.highlightText}>{{ title: itemTitle }}</div>

          {{ cycleType: _.toLower(cycleType) }}

          <div className={classes.highlightText}>{{ cycleName }}</div>
        </Trans>
      }
      tooltipContainerStyle={styles.tooltipContainerStyle}
      placement={"bottom"}
      alignConfig={{ offset: [4, -8] }}
    >
      <div className={classes.approvedPractice}>
        <StarFilled size={"xxx-small"} className={classes.starIcon} />
      </div>
    </LinkWithTooltip>
  );
};

const checkforEnclosingBracket = code => {
  const codeLength = _.size(code);
  return code.charAt(0) === "(" && code.charAt(codeLength - 1) === ")";
};

const TaggedPracticesV2 = props => {
  const {
    taggedPractices,
    t,
    showPracticeStatus,
    snpEvaluationCycles,
    postTitle,
    organizationId,
  } = props;

  const isAuthAndEvalRevampEnabled = ACLStore.can(ENABLE_AUTH_AND_EVAL_REVAMP);

  const requirementLabelSetting = getSettingValue({
    organizationId,
    name: "AppSettingSnpRequirementLabel",
  });

  const requirementLabel = t(`snp:${requirementLabelSetting}`, {
    defaultValue: requirementLabelSetting,
  });
  const requirementType = classNames(
    { [classes.type]: true },
    { ["text-body-2"]: true }
  );

  const taggedPracticeCode = classNames(
    { [classes.code]: !isAuthAndEvalRevampEnabled },
    { [classes.codeForAuthAndEvalRevamp]: isAuthAndEvalRevampEnabled },
    { ["text-label-3"]: !isAuthAndEvalRevampEnabled },
    { "text-label": isAuthAndEvalRevampEnabled }
  );

  const bullet = classNames(
    { [classes.bullet]: true },
    { ["text-body-2"]: true }
  );

  const practiceHeaderClasses = classNames(classes.practiceHeader, {
    [classes.practiceHeaderForAuthAndEvalRevamp]: isAuthAndEvalRevampEnabled,
  });

  const labelClasses = classNames({
    "text-body": isAuthAndEvalRevampEnabled,
  });

  return (
    <div className={classes.container}>
      {_.map(
        taggedPractices,
        ({ taggedPractice, evaluationCycle: { id: cycleId } }, index) => {
          const { title, cycleType } =
            _.find(snpEvaluationCycles, ["id", cycleId]) ?? {};

          return (
            <div className={classes.nodeContainer} key={index}>
              <div className={classes.practicesContainer}>
                {_.map(
                  taggedPractice,
                  ({
                    practice: { id, code, type, label, localizedLabel },
                    evidenceStatus,
                  }) => {
                    const codeWoBrackets = checkforEnclosingBracket(code)
                      ? code.slice(1, -1)
                      : code; //remove brackets from taggedpractice's code if they exist
                    return (
                      <div className={classes.practice} key={id}>
                        <div className={practiceHeaderClasses}>
                          <span className={taggedPracticeCode}>
                            {`${codeWoBrackets}`}
                          </span>
                          {isAuthAndEvalRevampEnabled ? (
                            <span className={classes.typeForAuthAndEvalRevamp}>
                              <Tag dsVersion="2.0" size="small">
                                {getTypeLabel({
                                  t,
                                  requirementLabel,
                                  type,
                                })}
                              </Tag>
                            </span>
                          ) : (
                            <>
                              <span className={bullet}>•</span>
                              <span
                                className={requirementType}
                              >{`${getTypeLabel({
                                t,
                                requirementLabel,
                                type,
                              })}`}</span>
                            </>
                          )}
                          {evidenceStatus === "APPROVED" &&
                            showPracticeStatus && (
                              <ApprovedPracticeMessage
                                itemTypeLabel={t("common:post")}
                                cycleType={cycleType}
                                cycleName={title}
                                itemTitle={postTitle || t("common:untitled")}
                                isAuthAndEvalRevampEnabled={
                                  isAuthAndEvalRevampEnabled
                                }
                              />
                            )}
                        </div>
                        <div className={labelClasses}>
                          {t(`snpTemplate:${label}`, {
                            defaultValue: localizedLabel,
                          })}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const organizationId = _.get(state, "login.userInfo.org_id", "");
  const currentCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    ""
  );

  const { snpEvaluationCycles } = getSnPEvaluationCyclesBasicDetailsFromCache({
    id: organizationId,
    filters: {
      isArchived: false,
      curriculumProgramId: currentCurriculumProgramId,
    },
  });

  return { snpEvaluationCycles, organizationId };
};

export default compose(
  I18nHOC({ resources: ["snpTemplate", "snp", "common"] }),
  connect(mapStateToProps)
)(TaggedPracticesV2);

TaggedPracticesV2.defaultProps = {
  showPracticeStatus: true,
};

TaggedPracticesV2.propTypes = {
  showPracticeStatus: PropTypes.bool,
};
